import { useEffect, useRef, useCallback, useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import type { ChatOverview, ChatMessageResponse, ScopeFilter, ScopeCursor } from '@/types/teacher-chat'
import { teacherChatApi } from '@/services/api/teacherChat'
import { ChatMessageBubble } from './ChatMessageBubble'
import { formatFullDate } from '@/utils/chatHelpers'

interface ChatMessagesProps {
  chat: ChatOverview
  currentTeacherId: string | undefined
  scopeFilter: ScopeFilter
  filterKey: string
}

const MESSAGES_PER_PAGE = 50

function groupByDate(messages: ChatMessageResponse[]): Record<string, ChatMessageResponse[]> {
  const groups: Record<string, ChatMessageResponse[]> = {}
  for (const m of messages) {
    const dateKey = m.created_at.split('T')[0] || 'unknown'
    if (!groups[dateKey]) groups[dateKey] = []
    groups[dateKey].push(m)
  }
  return groups
}

export function ChatMessages({ chat, currentTeacherId, scopeFilter, filterKey }: ChatMessagesProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null)
  const isInitialLoad = useRef(true)
  const lastMessageIdRef = useRef<string | null>(null)

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['chat-messages', chat.request_id, filterKey],
    queryFn: ({ pageParam }) =>
      teacherChatApi.getMessages(
        chat.request_id,
        MESSAGES_PER_PAGE,
        pageParam as ScopeCursor | undefined,
        scopeFilter
      ),
    getNextPageParam: (lastPage) => {
      // BL#3/#4: has_more steuert Pagination; composite cursor aus aeltester Nachricht.
      if (!lastPage.has_more || lastPage.messages.length === 0) return undefined
      const oldest = lastPage.messages[0]
      if (!oldest) return undefined
      return { before: oldest.created_at, before_id: oldest.id }
    },
    refetchInterval: () => (document.hidden ? false : 10000),
    initialPageParam: undefined as ScopeCursor | undefined,
  })

  const messages = useMemo(() => data?.pages.flatMap(p => p.messages) ?? [], [data])

  useEffect(() => {
    const last = messages[messages.length - 1]
    if (!last) return
    const isNewLast = last.id !== lastMessageIdRef.current
    lastMessageIdRef.current = last.id
    if (!isNewLast) return
    if (isInitialLoad.current || last.sender_id === currentTeacherId) {
      messagesEndRef.current?.scrollIntoView({ behavior: isInitialLoad.current ? 'auto' : 'smooth' })
      isInitialLoad.current = false
    }
  }, [messages, currentTeacherId])

  useEffect(() => {
    isInitialLoad.current = true
    lastMessageIdRef.current = null
  }, [chat.request_id])

  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const entry = entries[0]
    if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    const trigger = loadMoreTriggerRef.current
    if (!trigger) return

    const observer = new IntersectionObserver(handleIntersection, { threshold: 0.1 })
    observer.observe(trigger)
    return () => observer.disconnect()
  }, [handleIntersection])

  const grouped = groupByDate(messages)
  const participantMap = new Map(chat.participants.map(p => [p.teacher_id, p]))

  return (
    <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden p-4 md:p-6 bg-gray-50" role="log" aria-label="Chat-Verlauf">
      <div ref={loadMoreTriggerRef} className="h-1" />

      {hasNextPage && (
        <div className="text-center py-2">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="text-xs text-blue-600 hover:underline disabled:opacity-50"
          >
            {isFetchingNextPage ? 'Lade ältere Nachrichten...' : 'Ältere Nachrichten laden'}
          </button>
        </div>
      )}

      {messages.length === 0 && !isFetchingNextPage && (
        <div className="text-center py-12 text-gray-400">
          <p className="text-sm">Noch keine Nachrichten in diesem Chat.</p>
        </div>
      )}

      <div className="space-y-6">
        {Object.entries(grouped).map(([dateKey, msgs]) => (
          <div key={dateKey}>
            <div className="text-center mb-3">
              <span className="text-[10px] text-gray-400 bg-white px-3 py-1 rounded-full shadow-sm border border-gray-100">
                {formatFullDate(msgs[0]?.created_at ?? dateKey)}
              </span>
            </div>
            <div className="space-y-3">
              {msgs.map(msg => {
                const isOwn = msg.sender_id === currentTeacherId
                const participant = participantMap.get(msg.sender_id)
                const canDelete =
                  isOwn || chat.participants.some(p => p.is_current_user && p.is_requesting_teacher)
                return (
                  <ChatMessageBubble
                    key={msg.id}
                    message={msg}
                    participant={participant}
                    isOwn={isOwn}
                    requestId={chat.request_id}
                    canDelete={canDelete}
                  />
                )
              })}
            </div>
          </div>
        ))}
      </div>
      <div ref={messagesEndRef} />
    </div>
  )
}
